/** Persist Сводка / Версии query filters across tab switches (sessionStorage).
 * Section itself always comes from URL `tab` — never restore map/etc from storage.
 */

const DASHBOARD_QUERY_KEY = 'mira.risks.dashboardQuery';
const STATUS_QUERY_KEY = 'mira.risks.statusQuery';
const SECTION_KEY = 'mira.risks.section';
const SECTIONS = new Set(['dashboard', 'status', 'urgent', 'map', 'find', 'broadcast', 'rules']);

function canUseStorage() {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

function normalizeSearch(search) {
  if (!search) {
    return '';
  }
  const text = String(search);
  if (!text || text === '?') {
    return '';
  }
  return text.startsWith('?') ? text : `?${text}`;
}

function readKey(key) {
  if (!canUseStorage()) {
    return '';
  }
  try {
    return normalizeSearch(window.sessionStorage.getItem(key) || '');
  } catch {
    return '';
  }
}

function writeKey(key, search) {
  if (!canUseStorage()) {
    return;
  }
  try {
    const normalized = normalizeSearch(search);
    if (normalized) {
      window.sessionStorage.setItem(key, normalized);
    } else {
      window.sessionStorage.removeItem(key);
    }
  } catch {
    // ignore quota / private mode
  }
}

function stripTabParam(search) {
  const params = new URLSearchParams(normalizeSearch(search).replace(/^\?/, ''));
  params.delete('tab');
  const text = params.toString();
  return text ? `?${text}` : '';
}

export function rememberDashboardSearch(search) {
  writeKey(DASHBOARD_QUERY_KEY, stripTabParam(search));
}

export function rememberStatusSearch(search) {
  writeKey(STATUS_QUERY_KEY, stripTabParam(search));
}

export function loadDashboardSearch() {
  return readKey(DASHBOARD_QUERY_KEY);
}

export function loadStatusSearch() {
  return readKey(STATUS_QUERY_KEY);
}

export function rememberSection(section) {
  if (!canUseStorage()) {
    return;
  }
  try {
    window.sessionStorage.setItem(SECTION_KEY, SECTIONS.has(section) ? section : 'dashboard');
  } catch {
    // ignore
  }
}

export function loadSection(fallback = 'dashboard') {
  if (!canUseStorage()) {
    return fallback;
  }
  try {
    const value = window.sessionStorage.getItem(SECTION_KEY);
    return SECTIONS.has(value) ? value : fallback;
  } catch {
    return fallback;
  }
}

function withOrgFallback(search, fallbackSearch) {
  const params = new URLSearchParams(normalizeSearch(search).replace(/^\?/, ''));
  if (params.get('organizationId')) {
    return normalizeSearch(search);
  }
  const fallback = new URLSearchParams(normalizeSearch(fallbackSearch).replace(/^\?/, ''));
  const org = fallback.get('organizationId');
  if (org) {
    params.set('organizationId', org);
  }
  const text = params.toString();
  return text ? `?${text}` : '';
}

/** Build URLSearchParams for the target section; always includes `tab`. */
export function buildSectionSearchParams(section, { currentSearch = '' } = {}) {
  const safe = SECTIONS.has(section) ? section : 'dashboard';

  if (safe === 'status') {
    const restored = withOrgFallback(loadStatusSearch() || currentSearch, loadDashboardSearch());
    const params = new URLSearchParams(normalizeSearch(restored).replace(/^\?/, ''));
    params.set('tab', 'status');
    return params;
  }

  if (safe === 'urgent' || safe === 'map' || safe === 'find' || safe === 'broadcast' || safe === 'rules') {
    const params = new URLSearchParams();
    params.set('tab', safe);
    return params;
  }

  const restored = loadDashboardSearch() || stripTabParam(currentSearch);
  const params = new URLSearchParams(normalizeSearch(restored).replace(/^\?/, ''));
  params.set('tab', 'dashboard');
  return params;
}

/**
 * Section from URL only. Missing/invalid `tab` → dashboard.
 * Do not restore map/urgent from sessionStorage (that stuck the map after reload).
 */
export function resolveSectionFromSearchParams(searchParams) {
  const fromUrl = searchParams?.get?.('tab');
  if (SECTIONS.has(fromUrl)) {
    return fromUrl;
  }
  return 'dashboard';
}

/** @deprecated use resolveSectionFromSearchParams */
export function resolveInitialSection(searchParams) {
  return resolveSectionFromSearchParams(searchParams);
}
